<script>
import "./videojs/controls/RecordIndicator";
import VideoPlayer from "./VideoPlayer";
import PlayerControls from "./PlayerControls";

import Recorder from "@/services/videos/Recorder";
// import { RecordModel } from "@/services/indexedDb";
import service from "@/services/contest/videos";

export default {
  name: "Player",
  components: {
    VideoPlayer,
    PlayerControls
  },

  props: {
    video: String,
    audio: String,
    videoId: Number
  },

  provide() {
    return {
      playerInstance: this
    };
  },

  inject: ["modalsGroup"],

  data() {
    return {
      recorder: new Recorder(),
      playerOptions: {
        width: 640,
        height: 360,
        preload: "auto",
        fluid: true,
        responsive: true,
        controls: true,
        bigPlayButton: false,
        controlBar: {
          children: [
            "playToggle",
            "ProgressControl",
            "remainingTimeDisplay",
            "FullscreenToggle",
            "RecordIndicator"
          ]
        }
      },

      player: null,
      recordedAudio: null,

      playing: false,
      paused: false,
      ended: false,
      readyState: null,
      currentPlayerSource: null,

      videoLoaded: false,
      audioLoaded: false
    };
  },

  computed: {
    videoFileName() {
      return `/media/video/${this.video.split("/").pop()}`;
    },
    sources() {
      return [
        {
          type: "video/mp4; codecs=avc1.4D401E,mp4a.40.2",
          src: this.video
        },
        {
          type: "video/webm",
          src: this.video.replace(".mp4", ".webm")
        }
      ];
    },
    videoOpts() {
      return {
        ...this.playerOptions,
        sources: this.sources
      };
    },
    audioNodeSrc() {
      if (this.audio) {
        return this.audio;
      }

      return this.recordedAudio;
    },

    // audioNodeAttrs() {
    //   audioNode
    // },

    timePlayed() {
      return (this.player && this.player.currentTime()) || 0;
    },

    videoEnded() {
      return this.paused && this.ended;
    },

    videoNotStarted() {
      return (
        this.timePlayed <= 0 && this.paused && !this.ended && !this.recording
      );
    },

    videoPreviewPlaying() {
      return this.timePlayed >= 0 && this.playing && !this.recording;
    },

    videoPreviewPaused() {
      return (
        this.timePlayed > 0 && this.paused && !this.ended && !this.recording
      );
    },

    showControlsOverlay() {
      return this.videoNotStarted || this.videoPreviewPaused || this.videoEnded;
    },

    showControlsBtn() {
      return this.readyState >= 2 && !this.playing;
    },

    recording() {
      return ["recording", "paused"].includes(this.recorder.status);
    },

    recorded() {
      return this.recorder.status === "stopped";
    },

    statuses() {
      const list = ["playing", "paused", "ended", "recording", "recorded"];

      const statuses = {};
      list.forEach(k => {
        statuses[k] = this[k];
      });

      return Object.assign(statuses, {
        listening: this.videoPreviewPlaying,
        listenPaused: this.videoPreviewPaused,
        overlay: this.showControlsOverlay,
        videoLoaded: this.videoLoaded
      });
    },

    styles() {
      return {};
    }
  },

  watch: {
    statuses: {
      deep: true,
      handler: function(after) {
        const { controlBar } = this.player;

        if (
          after.playing ||
          after.listenPaused ||
          (after.videoLoaded && !after.overlay)
        ) {
          controlBar.show();
        } else {
          controlBar.hide();
        }

        if (after.listening) {
          controlBar.playToggle.enable();
          controlBar.ProgressControl.enable();
        }

        if (after.recording) {
          controlBar.playToggle.disable();
          controlBar.ProgressControl.disable();
          controlBar.RecordIndicator.show();
        } else {
          controlBar.RecordIndicator.hide();
        }
      }
    }
  },

  mounted() {
    // setTimeout(() => {
    //   this.player && this.video && this.setVideoSrc(this.video);
    // }, 100);
    // this.audio && this.setAudioSrc(this.audio);
  },

  async beforeDestroy() {
    // this.setRecordedAudio();
    if (this.recorder) {
      await this.recorder.dispose();
      this.recorder = null;
    }
  },

  methods: {
    async getOrSetBlob(model, url) {
      const exist = await model.get(url);
      if (exist) return exist.blob;

      const blob = await fetch(url).then(r => r.blob());
      const item = new model({ url, blob });
      item.put();

      return blob;
    },

    async urlToBlob(src, model, asSource = false) {
      const blob = await this.getOrSetBlob(model, src);
      if (!asSource) return blob;

      return {
        withCredentials: false,
        src: URL.createObjectURL(blob),
        type: blob.type
      };
    },

    // async setVideoSrc(src) {
    //   this.videoLoaded = false;
    //   const source = await this.urlToBlob(src, RecordModel, true);
    //   this.player.reset();
    //   this.player.src(source);
    //   this.videoLoaded = true;
    // },

    // async setAudioSrc(src) {
    //   const remoteRecordBlob = await this.urlToBlob(src, RecordModel);
    //   return this.setRecordedAudio(remoteRecordBlob);
    // },

    setRecordedAudio(url = null) {
      if (this.$refs.audioNode && !this.$refs.audioNode.paused) {
        this.$refs.audioNode.pause();
      }

      if (url instanceof Blob || url instanceof File) {
        url = URL.createObjectURL(url);
      }

      this.recordedAudio = url;

      // if (blob) {
      //   const audio = document.createElement("audio");
      //   this.recorder.load(blob, audio);
      //   audio.controls = false;
      //   audio.autoplay = false;
      //   this.recordedAudio = audio;
      // }

      return this.recordedAudio;
    },

    onPlayerReady(player) {
      player.loop(false);

      // the native controls don't work for this UI so disable
      // them no matter what
      if (player.usingNativeControls_ === true) {
        if (player.tech_.el_ !== undefined) {
          player.tech_.el_.controls = false;
        }
      }
      // clicking or tapping the player video element should not try
      // to start playback
      player.removeTechControlsListeners_();

      if (player.options_.controls) {
        // prevent controlbar fadeout
        player.on("userinactive", () => {
          player.userActive(true);
        });
      }

      player.controlBar.hide();

      this.player = player;
    },

    async onPlay() {
      if (this.recorder.status == "paused") {
        await this.recorder.resume();
      } else if (this.$refs.audioNode && this.$refs.audioNode.paused) {
        this.$refs.audioNode.play();
      }
    },

    async onPause() {
      if (this.recorder.status == "recording") {
        await this.recorder.pause();
      } else if (this.$refs.audioNode && !this.$refs.audioNode.paused) {
        this.$refs.audioNode.pause();
      }
    },

    async onEnded() {
      if (this.recording) {
        await this.recorder.stop();
        await this.finishRecord();

        this.setClosableModal();
      } else if (this.$refs.audioNode && this.$refs.audioNode.paused) {
        this.$refs.audioNode.pause();
      }
    },

    async onTimeupdate() {
      if (this.$refs.audioNode && this.$refs.audioNode.readyState >= 4) {
        if (
          Math.ceil(this.$refs.audioNode.currentTime) !=
          Math.ceil(this.timePlayed)
        ) {
          this.$refs.audioNode.currentTime = this.timePlayed;
        }
      }
    },

    async finishRecord() {
      // let video = this.video; // await fetch(this.src).then(r => r.blob());
      let blob = this.recorder.recordedData;

      if (blob) {
        await this.setRecordedAudio(blob);
      }

      // const model = new RecordModel({ video, blob })
      // await model.put()
      // console.log(model)
    },

    onStateChanged(state) {
      console.log("onStateChanged", state);
      this.playing = !this.player.paused() && !this.player.ended();
      this.paused = this.player.paused();
      this.ended = this.player.ended();
      this.readyState = this.player.el_.firstChild.readyState;

      this.currentPlayerSource = this.player.el_.firstChild.currentSrc;
    },

    getVideoPlayerListeners() {
      const listeners = {};
      listeners.ready = this.onPlayerReady;
      listeners.statechanged = this.onStateChanged;

      listeners.play = this.onPlay;
      listeners.playing = this.onPlay;

      listeners.pause = this.onPause;
      listeners.waiting = this.onPause;

      listeners.ended = this.onEnded;
      listeners.timeupdate = this.onTimeupdate;

      return listeners;
    },

    genVideoPlayer() {
      return this.$createElement("VideoPlayer", {
        props: {
          playsinline: true,
          options: this.videoOpts
        },
        on: this.getVideoPlayerListeners(),
        ref: "videoPlayer"
      });
    },

    genControls() {
      return this.$createElement("PlayerControls", {
        staticClass: "media-player__wrap",
        props: {
          noOverlay: !this.showControlsOverlay,
          disabled: !this.showControlsBtn
        }
      });
    },

    genAudioNode() {
      if (!this.audioNodeSrc) return null;

      return this.$createElement("audio", {
        attrs: {
          controls: false,
          autoplay: false,
          plreload: "auto",
          src: this.audioNodeSrc
        },
        ref: "audioNode"
      });
    },

    genContent() {
      return [
        this.genVideoPlayer(),
        this.genControls(),
        this.genAudioNode()
        // this.$createElement('pre', {
        //   class: "player-debug"
        // }, [
        //   'Debug info:',
        //   '\nReadyState: '+this.readyState,
        //   '\nCurrentPlayerSource: '+this.currentPlayerSource,
        //   '\n\n'+(this.player && this.player.log.history().length > 0 ? this.player.log.history().pop() : '')
        // ])
      ];
    },

    setClosableModal(val = true) {
      if (!this.modalsGroup || !this.modalsGroup.current) return;
      this.modalsGroup.current.closable = val;
      this.modalsGroup.current.closeOnBackdrop = val;
    },

    play() {
      this.player.play();
    },

    pause() {
      this.player.pause();
    },

    stop() {
      this.player.currentTime(0);
      this.player.pause();
    },

    async record() {
      this.recordedAudio = null;
      this.stop();
      this.setClosableModal(false);
      await this.recorder.createRecorder();
      await this.recorder.start();
      this.play();
    },

    async upload(blob) {
      if (!blob) {
        const { convertedData, recordedData } = this.recorder;
        blob = convertedData || recordedData;
      }
      return service.uploadRecord(Recorder.getFileObject(blob), this.videoId);
      // const body = new FormData();
      // body.append(
      //   "file",
      //   Recorder.getFileObject(blob || this.recorder.recordedData)
      // );

      // const { data } = await axios.post("//localhost:3000/upload", body);
      // console.log(data);
    }
  },

  render(h) {
    return h(
      "div",
      {
        staticClass: "media-player",
        style: this.styles
      },
      this.genContent()
    );
  }
};
</script>

<style lang="scss">
.media-player {
  position: relative;
  flex: auto;
  max-width: 900px;

  &__wrap {
    position: absolute;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
  }

  .player-debug {
    position: relative;
    z-index: 100;
    white-space: pre-wrap;
  }
}
</style>
